import type { InGridConfig } from "@/utils/ingridConfig"

/**
 * Total grid cells reserved by in-grid blocks on the first page. Each in-grid
 * tile spans `columnSpan` cells (1, 2, or 4) — summing the spans gives the
 * number of product slots the tiles will visually occupy.
 *
 * In-grid blocks render on the first page only, so subsequent pages keep the
 * standard `pageSize` of products.
 */
export function computeInGridSlotCount(configs: readonly InGridConfig[]): number {
  return configs.reduce((sum, c) => sum + c.columnSpan, 0)
}

/**
 * Page slicing aware of first-page in-grid blocks. The first page reserves
 * `slots` grid cells for the tiles, so it shows at most `pageSize - slots`
 * products. Subsequent pages render a full `pageSize` of products.
 *
 * Returns `[start, end)` indices into a deduped product list.
 */
export function pageSliceWithInGrid(page: number, pageSize: number, slots: number): { start: number; end: number } {
  if (pageSize <= 0) return { start: 0, end: 0 }
  if (slots <= 0) {
    const start = Math.max(0, (page - 1) * pageSize)
    return { start, end: start + pageSize }
  }
  // Clamp to [0, pageSize] — first page can't reserve more cells than it has.
  const safeSlots = Math.max(0, Math.min(slots, pageSize))
  const firstPageCap = pageSize - safeSlots
  if (page <= 1) {
    return { start: 0, end: firstPageCap }
  }
  const start = firstPageCap + (page - 2) * pageSize
  return { start, end: start + pageSize }
}

/**
 * Total page count when the first page reserves `slots` cells for in-grid
 * tiles. With no slots this matches `Math.ceil(total / pageSize)`.
 */
export function totalPagesWithInGrid(total: number, pageSize: number, slots: number): number {
  if (total <= 0 || pageSize <= 0) return 0
  const safeSlots = Math.max(0, Math.min(slots, pageSize))
  const firstPageCap = pageSize - safeSlots
  if (firstPageCap >= total) return 1
  return 1 + Math.ceil((total - firstPageCap) / pageSize)
}

/**
 * Products visible on a given page: `firstPageCap` on page 1, otherwise the
 * full pageSize (or fewer on the last page if it doesn't fill).
 */
export function pageProductCount(page: number, pageSize: number, slots: number, total: number): number {
  if (pageSize <= 0 || total <= 0) return 0
  const { start, end } = pageSliceWithInGrid(page, pageSize, slots)
  return Math.max(0, Math.min(end, total) - Math.min(start, total))
}
